/**
 * Audio clip sync bridge — persists audio clips from the web app back to
 * Unity's `timeline_audioclips` SQLite table via the URL-scheme bridge.
 *
 * Two message types:
 * - `syncAudioMeta`     Full clip metadata array (debounced 500ms)
 * - `setAudioBlobChunk` Chunked base64 binary for one new clip (512KB raw per chunk)
 */

import { enqueueSendToNative } from './embeddedBridge.ts';
import { MAX_BATCH_BYTES, outboxAudioBlobChunkClipIds, dropOutboxAudioBlobChunks } from './transport.ts';
import { getClipAbsoluteStart } from '@/store/projectDerived.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import { getBlob } from '@/previs/audioBlobStore.ts';
import type { AudioClip } from '@/types/timeline.ts';

// ─── Tracking sets ──────────────────────────────────────────────────────

/** Clip IDs that were loaded from Unity (already have BLOBs in the DB). */
const unityKnownClipIds = new Set<number>();

/** Clip IDs whose binary has already been sent to Unity via chunks. */
const sentBinaryClipIds = new Set<number>();

// ─── Debounced metadata sync ────────────────────────────────────────────

let metaSyncTimer: ReturnType<typeof setTimeout> | null = null;
let unsubscribe: (() => void) | null = null;

function scheduleMetaSync(): void {
  if (metaSyncTimer != null) clearTimeout(metaSyncTimer);
  metaSyncTimer = setTimeout(flushMetaSync, 500);
}

function flushMetaSync(): void {
  metaSyncTimer = null;
  const clips = useAudioStore.getState().audioClips;
  const metaClips = clips.map(clipToMeta);
  enqueueSendToNative('syncAudioMeta', { clips: metaClips });
}

function clipToMeta(c: AudioClip): Record<string, unknown> {
  const project = useProjectStore.getState().project;
  return {
    id: c.id,
    label: c.label,
    shotId: c.shotId,
    shotOffset: c.shotOffset,
    startTime: project ? getClipAbsoluteStart(c, project) : c.shotOffset,
    duration: c.duration,
    volume: c.volume,
    bufferOffset: c.bufferOffset,
    mimeType: c.mimeType,
    lane: c.lane,
    isMuted: c.isMuted,
    fadeInTime: c.fadeInTime,
    fadeOutTime: c.fadeOutTime,
    color: c.color,
    cdnUrl: c.cdnUrl ?? null,
    hasBlob: unityKnownClipIds.has(c.id) || sentBinaryClipIds.has(c.id),
  };
}

// ─── Chunked binary transfer ────────────────────────────────────────────

/**
 * Maximum base64 *characters* per chunk message.
 * ~700K chars ≈ 512KB raw data after base64 expansion.
 */
const B64_CHUNK_CHARS = 700 * 1024;

import { uint8ToBase64 } from '@/utils/binaryUtils.ts';

/**
 * Send a clip's raw audio bytes to Unity as chunked base64.
 * Called from audioStore after addAudioClip / splitAudioClip.
 */
export function sendAudioBlobToUnity(clipId: number): void {
  if (sentBinaryClipIds.has(clipId) || unityKnownClipIds.has(clipId)) return;

  const raw = getBlob(clipId);
  if (!raw || raw.length === 0) return;

  const clip = useAudioStore.getState().audioClips.find(c => c.id === clipId);

  if (clip?.cdnUrl) {
    // Unity will download natively from the CDN URL via metadata sync
    sentBinaryClipIds.add(clipId);
    return;
  }

  const mimeType = clip?.mimeType ?? 'audio/wav';
  sendChunked(clipId, raw, mimeType);

  // Immediately flush metadata so Unity has the clip row alongside the blob.
  // Without this, the 500ms debounce could leave Unity without the metadata
  // if the WebView reloads before the timer fires.
  if (metaSyncTimer != null) clearTimeout(metaSyncTimer);
  flushMetaSync();
}

function sendChunked(clipId: number, data: Uint8Array, mimeType: string): void {
  sentBinaryClipIds.add(clipId);

  // Encode the entire blob as ONE base64 string, then split the STRING
  // into chunks.  Previously each raw-byte chunk was base64-encoded
  // independently, which put padding characters in the middle of the
  // concatenated string — causing C#'s Convert.FromBase64String to fail.
  const fullB64 = uint8ToBase64(data);
  const totalChunks = Math.ceil(fullB64.length / B64_CHUNK_CHARS);

  for (let i = 0; i < totalChunks; i++) {
    const start = i * B64_CHUNK_CHARS;
    const end = Math.min(start + B64_CHUNK_CHARS, fullB64.length);

    enqueueSendToNative('setAudioBlobChunk', {
      clipId,
      chunkIndex: i,
      totalChunks,
      mimeType,
      data: fullB64.slice(start, end),
    });
  }
}

// ─── Public API ─────────────────────────────────────────────────────────

/** Mark a clip ID as already persisted in Unity's database (e.g., from native recording). */
export function markClipAsUnityKnown(clipId: number): void {
  unityKnownClipIds.add(clipId);
}

/**
 * Tear down audio sync — unsubscribes store listener, cancels pending timer,
 * and removes the pagehide listener. Safe to call multiple times.
 */
export function teardownAudioSync(): void {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  if (metaSyncTimer != null) {
    clearTimeout(metaSyncTimer);
    metaSyncTimer = null;
  }
  window.removeEventListener('pagehide', onPageHide);
}

function onPageHide(): void {
  flushAudioSync();
  teardownAudioSync();
}

/**
 * Initialize audio sync. Call once from loadProject() in bridge mode.
 * Registers a Zustand subscription for debounced metadata sync.
 */
export function initAudioSync(initialClipIds: number[]): void {
  // Clean up any previous subscription (e.g. project reload)
  teardownAudioSync();

  unityKnownClipIds.clear();
  sentBinaryClipIds.clear();
  for (const id of initialClipIds) {
    unityKnownClipIds.add(id);
  }

  // Subscribe to audioStore — only fire when clip metadata changes (not playback position etc.)
  let prevClips = useAudioStore.getState().audioClips;
  unsubscribe = useAudioStore.subscribe((state) => {
    if (state.audioClips !== prevClips) {
      prevClips = state.audioClips;
      scheduleMetaSync();
    }
  });

  // Clean up if WebView is torn down without re-init
  window.addEventListener('pagehide', onPageHide);

  // Register global flush function for Unity to call before panel close
  (window as unknown as Record<string, unknown>).__flushAudioSync = flushAudioSync;
}

/**
 * Flush any pending debounced metadata sync AND send any unsent audio blobs.
 * Called before panel close / team upload so Unity gets the latest state.
 *
 * When __previsForceSync is true (a teardown drain), see flushAudioSyncForTeardown below: it stashes
 * ONE `audioFlushBundle` for the drain's single navigation, and only when something is genuinely pending.
 *
 * When __previsForceSync is false (normal operation / team upload coroutine),
 * we use the existing outbox path which drains correctly.
 */
export function flushAudioSync(): void {
  const win = window as unknown as Record<string, unknown>;
  if (win.__previsForceSync) {
    flushAudioSyncForTeardown();
    return;
  }

  // Cancel any pending debounced meta sync
  if (metaSyncTimer != null) {
    clearTimeout(metaSyncTimer);
    metaSyncTimer = null;
  }

  const clips = useAudioStore.getState().audioClips;

  // Non-force-sync path: use outbox (team upload coroutine drains it)
  for (const clip of clips) {
    if (!sentBinaryClipIds.has(clip.id) && !unityKnownClipIds.has(clip.id) && !clip.cdnUrl) {
      const raw = getBlob(clip.id);
      if (raw && raw.length > 0) {
        sendChunked(clip.id, raw, clip.mimeType ?? 'audio/wav');
      }
    }
  }
  // Always send meta — ensure Unity has rows for all clips
  flushMetaSync();
}

/**
 * 20261005 - THE TEARDOWN AUDIO FLUSH: ONLY WHAT IS GENUINELY PENDING.
 *
 * flushAudioSync's force-sync half, run inside Unity's teardown drain (TimelineWebViewPanel.BoardFlushDrainScript),
 * whose one navigation now really arrives. It stashes exactly what normal operation would have sent a moment later:
 *   - The metadata list, only when its debounced sync was pending. Unity's handler treats the list as the whole truth
 *     (it updates every clip and DELETES every row missing from it), so the list is always the full store, and an empty
 *     list is legitimate only here: the user just deleted the last clip. It used to go on EVERY drain, and an empty store
 *     fell through to flushMetaSync, whose `syncAudioMeta {clips: []}` deleted every timeline_audioclips row once the
 *     bundle stopped being dropped: an audio store that failed to load would have erased the project's audio on the next
 *     canvas entry or close.
 *   - The bytes of (a) clips never handed to the transport (not Unity-known, no cdnUrl, not sent) and (b) clips whose
 *     setAudioBlobChunk messages are still waiting in the outbox, which would die with the page. Not every web-created
 *     clip: that re-encoded megabytes of base64 into every drain and could push the one navigation past the URL ceiling.
 * Nothing pending: nothing is stashed and nothing is enqueued. It never falls through to the outbox path. A bundle whose
 * JSON would pass MAX_BATCH_BYTES goes without its bytes: those clips are not marked sent and their queued chunks stay
 * queued, for the outbox to carry if the page lives on. When (b) bytes do go in the bundle, their queued chunks are
 * dropped so the clip is not written twice.
 */
function flushAudioSyncForTeardown(): void {
  // Recorded BEFORE the debounce is cancelled: a pending timer is what says the metadata has changed since Unity's copy.
  const metaSyncWasPending = metaSyncTimer != null;
  if (metaSyncTimer != null) {
    clearTimeout(metaSyncTimer);
    metaSyncTimer = null;
  }

  const clips = useAudioStore.getState().audioClips;
  const chunksWaitingClipIds = outboxAudioBlobChunkClipIds();
  const blobs: Array<{ clipId: number; mimeType: string; data: string }> = [];
  const blobsWithChunksWaiting = new Set<number>();

  for (const clip of clips) {
    if (unityKnownClipIds.has(clip.id) || clip.cdnUrl) continue;
    const neverSent = !sentBinaryClipIds.has(clip.id);
    const chunksWaiting = chunksWaitingClipIds.has(clip.id);
    if (!neverSent && !chunksWaiting) continue;
    const raw = getBlob(clip.id);
    if (!raw || raw.length === 0) continue;
    blobs.push({ clipId: clip.id, mimeType: clip.mimeType ?? 'audio/wav', data: uint8ToBase64(raw) });
    if (chunksWaiting) blobsWithChunksWaiting.add(clip.id);
  }

  if (!metaSyncWasPending && blobs.length === 0) return;

  // The full list, built before any blob below is marked sent (as the bundle always was).
  const metaClips = clips.map(clipToMeta);

  let bundle = JSON.stringify({ action: 'audioFlushBundle', blobs, clips: metaClips });
  if (blobs.length > 0 && bundle.length > MAX_BATCH_BYTES) {
    bundle = JSON.stringify({ action: 'audioFlushBundle', blobs: [], clips: metaClips });
  } else {
    for (const b of blobs) {
      sentBinaryClipIds.add(b.clipId);
    }
    if (blobsWithChunksWaiting.size > 0) dropOutboxAudioBlobChunks(blobsWithChunksWaiting);
  }

  // Stashed for the drain's one navigation (__collectAndSendForceFlush), never navigated or enqueued from here.
  (window as unknown as Record<string, unknown>).__pendingForceFlushAudio = bundle;
}
